import type { MouseEvent } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import { userAppContext } from '../../../../context/Context';
import { UserAppContextType } from '../../../../router/types';
import type { Items } from '../../../../types';
import {
  isNewTabModifierClick,
  isSearchWorkspacePath,
  useSearchTabNavigation,
} from '@/hooks/search/useSearchTabNavigation';
import { useLocation } from 'react-router-dom';

type LinksExamplesProps = {
  searchItems?: Items[];
  setSearchItems?: (fn: (prev: Items[]) => Items[]) => void;
};

const EXAMPLES = [
  { label: 'Obesity', view: 'DISEASES', ident: 'C0028754', tab: 'GDA' },
  { label: 'LEPR', view: 'GENES', ident: '3953', tab: 'GDA' },
  { label: 'rs121913279', view: 'VARIANTS', ident: 'rs121913279', tab: 'VDA' },
  { label: 'Streptozocin', view: 'CHEMICALS', ident: 'CHEMBL1651906', tab: 'GDA' },
] as const;

export const LinksExamples = ({ searchItems = [] }: LinksExamplesProps) => {
  const location = useLocation();
  const { navigateSearch } = useSearchTabNavigation();
  const { restrictSources } = userAppContext() as unknown as UserAppContextType;

  if (searchItems.length > 0) return null;

  const handleExampleClick = (
    { view, ident, tab, label }: { view: string; ident: string; tab: string; label: string },
    evt: MouseEvent,
  ) => {
    const source = restrictSources === 'CURATED' ? 'CURATED' : 'ALL';
    const search = `?view=${view}&idents=${ident}&source=${source}&tab=${tab}`;
    const openInNewTab = !isSearchWorkspacePath(location.pathname) || isNewTabModifierClick(evt);
    const result = navigateSearch({
      search,
      title: label,
      openInNewTab,
    });
    if (!result.ok && openInNewTab) {
      navigateSearch({ search, title: label, openInNewTab: false });
    }
  };

  return (
    <div className="search-examples">
      <span className="search-examples__label">Examples:</span>
      <div className="search-examples__tags">
        {EXAMPLES.map(example => (
          <button
            key={example.ident}
            type="button"
            className="search-dropdown-chip"
            data-category={example.view}
            onMouseDown={event => event.preventDefault()}
            onClick={evt =>
              handleExampleClick(
                {
                  view: example.view,
                  ident: example.ident,
                  tab: example.tab,
                  label: example.label,
                },
                evt,
              )
            }
          >
            <span className="search-dropdown-chip__dot" aria-hidden />
            <span className="search-dropdown-chip__label">{example.label}</span>
            <ArrowTopRightOnSquareIcon className="search-dropdown-chip__remove" aria-hidden />
          </button>
        ))}
      </div>
    </div>
  );
};
